/**
 * App 内热更新(只换网页包,不换原生壳)—— 司机端、管理端共用。
 *
 * 流程:启动 / 回到前台 → 拉 manifest → 版本不同就后台下载 →
 * `next()` 排队,**下次切到后台或重启时**才换上。
 *
 * ★ 不用 `set()` 立刻换。司机可能正在拍签收照、填代收金额,
 *   页面当场重载等于把他手上的东西扔掉。切后台再换,
 *   他回来看到的就已经是新版本。
 *
 * ★ 回滚靠插件:新包启动后 10 秒内没调 `notifyAppReady()`,
 *   插件判它坏了、退回上一个能用的包,并把它标成 error。
 *   这里看到 error 的版本就不再下 —— 否则会「下载 → 起不来 → 回滚 → 再下载」地转圈。
 *
 * ★ 版本号只比「相不相等」,不比大小。manifest 写哪个版本,App 就换成哪个 ——
 *   线上出事时把 manifest 指回上一版,就是回滚。
 *
 * ★ 改了原生层(加插件、改权限文案)的网页包,不能推给旧壳:
 *   新代码调一个壳里没有的插件,只会在那一步静默失败。
 *   manifest 里的 `minNative` 就是挡这个的。
 *
 * 这里只有逻辑,不 import 插件 —— 插件是各端的依赖,由调用方注入。
 */

export interface BundleManifest {
  version: string;
  /** zip 地址,相对 manifest 解析 */
  url: string;
  /** zip 的 sha256(hex)—— 插件下载完会校验 */
  sha256: string;
  /** 需要的最低原生壳版本 */
  minNative?: string;
}

/** 插件里用得到的那几个方法 —— 测试里换成假的 */
export interface Updater {
  notifyAppReady(): Promise<unknown>;
  list(): Promise<{ bundles: { id: string; version: string; status: string }[] }>;
  download(o: { url: string; version: string; checksum?: string }): Promise<{ id: string }>;
  next(o: { id: string }): Promise<unknown>;
  /** 有排队的包就换上它,并重载页面 */
  reload(): Promise<void>;
}

export type CheckResult =
  | 'up-to-date'
  | 'native-too-old'
  | 'known-bad'
  | 'staged'
  | 'bad-manifest';

/** `1.2.10` > `1.2.9`;缺的段当 0 */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((x) => parseInt(x, 10) || 0);
  const pb = b.split('.').map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

function isManifest(m: unknown): m is BundleManifest {
  const o = m as Record<string, unknown> | null;
  return (
    !!o &&
    typeof o['version'] === 'string' &&
    o['version'] !== '' &&
    typeof o['url'] === 'string' &&
    typeof o['sha256'] === 'string' &&
    /^[0-9a-f]{64}$/.test(o['sha256']) &&
    (o['minNative'] === undefined || typeof o['minNative'] === 'string')
  );
}

/** 一次检查。抛错(网络、下载失败)由调用方吞掉 —— 下次回前台再试 */
export async function checkForUpdate(deps: {
  updater: Updater;
  manifestUrl: string;
  current: string;
  nativeVersion: string | null;
  fetchJson?: (url: string) => Promise<unknown>;
  /** 拿到合法 manifest 时告诉调用方是哪个版本 —— 「稍后」要按版本记 */
  onVersion?: (version: string) => void;
}): Promise<CheckResult> {
  const fetchJson =
    deps.fetchJson ??
    (async (url: string) => {
      // `cache` 是浏览器 fetch 的字段 —— 这段跑在 WebView 里,而 shared 的类型按 Node 编译。
      // 不加 no-store 的话 WebView 会缓存 manifest,发了新版也一直说「已是最新」。
      const res = await fetch(url, { cache: 'no-store' } as RequestInit);
      if (!res.ok) throw new Error(`manifest ${res.status}`);
      return res.json();
    });

  const m = await fetchJson(deps.manifestUrl);
  if (!isManifest(m)) return 'bad-manifest';
  deps.onVersion?.(m.version);
  if (m.version === deps.current) return 'up-to-date';
  if (m.minNative && deps.nativeVersion && compareVersions(deps.nativeVersion, m.minNative) < 0) {
    return 'native-too-old';
  }

  const { bundles } = await deps.updater.list();
  const same = bundles.filter((b) => b.version === m.version);
  if (same.some((b) => b.status === 'error')) return 'known-bad';

  // 上次已经下好了(比如下完还没切后台就被杀了)—— 直接排队,不重下
  const ready = same.find((b) => b.status === 'success' || b.status === 'pending');
  const id =
    ready?.id ??
    (
      await deps.updater.download({
        url: new URL(m.url, deps.manifestUrl).toString(),
        version: m.version,
        checksum: m.sha256,
      })
    ).id;
  await deps.updater.next({ id });
  return 'staged';
}

export interface LiveUpdateDeps {
  /** 是不是原生壳里 */
  isNative: boolean;
  /** 构建时写进来的本包版本;本地 dev / 网页版是 'dev' */
  bundleVersion: string;
  /** manifest 地址;没配就不检查 */
  updateUrl: string;
  /**
   * 加载插件。
   * ★ 要包一层对象返回,不能直接返回插件:Capacitor 的插件是个 Proxy,
   *   任何属性名都当成原生方法 —— 包括 `then`。async 函数直接返回它,
   *   `await` 会去调它的 `.then()`,而那个「原生方法」永远不回,整条链卡死、不报错。
   */
  loadUpdater: () => Promise<{ updater: Updater } | null>;
  nativeAppVersion: () => Promise<string | null>;
  /** 回到前台时回调 */
  onAppResume: (cb: () => void) => Promise<unknown>;
  /** 开着不动时多久查一次;默认 30 分钟 */
  intervalMs?: number;
}

/**
 * 给界面看的更新状态。
 * - ready:新版本已经下好、排着队,点「立即更新」或切后台就换上
 * - reinstall:有新版本,但这个壳太旧,得重新装 App
 * `dismissed`:这个版本的提示条已经被点过「稍后」
 */
export type UpdateStatus =
  | { kind: 'none' }
  | { kind: 'ready' | 'reinstall'; version: string; dismissed: boolean };

export function createLiveUpdate(deps: LiveUpdateDeps) {
  const enabled = deps.isNative && !!deps.updateUrl && deps.bundleVersion !== 'dev';

  type Ctx = { updater: Updater; nativeVersion: string | null };
  let ctx: Promise<Ctx | null> | null = null;
  let inflight: Promise<CheckResult> | null = null;
  let started = false;

  /*
   * 状态 + 订阅。各端用 useSyncExternalStore 接 —— 所以状态对象只在**真变了**时换新的,
   * 否则每次检查都返回一个新对象,React 会以为一直在变、反复重渲染。
   */
  let status: UpdateStatus = { kind: 'none' };
  const dismissed = new Set<string>();
  const listeners = new Set<() => void>();
  const setStatus = (next: UpdateStatus) => {
    const same =
      next.kind === status.kind &&
      (next.kind === 'none' ||
        (status.kind !== 'none' && next.version === status.version && next.dismissed === status.dismissed));
    if (same) return;
    status = next;
    listeners.forEach((l) => l());
  };

  const getCtx = (): Promise<Ctx | null> => {
    ctx ??= (async () => {
      const loaded = await deps.loadUpdater();
      if (!loaded) return null;
      return { updater: loaded.updater, nativeVersion: await deps.nativeAppVersion() };
    })();
    return ctx;
  };

  /**
   * 检查一次。自动检查(回前台)和「检查更新」按钮走同一个入口。
   *
   * ★ 同一时刻只跑一次:切回前台的同时点了按钮,两边拿到的是同一个结果,
   *   而不是并发下同一个包两遍、`next()` 两次。
   *
   * 网络失败、下载失败会抛 —— 自动检查吞掉,按钮那边要说「检查失败」。
   */
  const checkNow = (): Promise<CheckResult> => {
    inflight ??= (async () => {
      const c = await getCtx();
      if (!c || !enabled) return 'up-to-date' as const;
      let version = '';
      const r = await checkForUpdate({
        updater: c.updater,
        manifestUrl: deps.updateUrl,
        current: deps.bundleVersion,
        nativeVersion: c.nativeVersion,
        onVersion: (v) => (version = v),
      });
      console.info(`[liveUpdate] ${deps.bundleVersion} → ${r}`);
      if (r === 'staged' || r === 'native-too-old') {
        setStatus({
          kind: r === 'staged' ? 'ready' : 'reinstall',
          version,
          dismissed: dismissed.has(version),
        });
      } else if (r === 'up-to-date' || r === 'known-bad') {
        // manifest 指回了当前版本(回滚)或坏包 —— 之前的提示要撤掉
        setStatus({ kind: 'none' });
      }
      // bad-manifest:不知道发生了什么,保持原状
      return r;
    })().finally(() => {
      inflight = null;
    });
    return inflight;
  };

  /**
   * 马上换上已下载的新版本(页面会重载)。
   *
   * ★ 只给「检查更新」按钮用 —— 那是用户自己点的,他知道页面要刷新。
   *   自动流程永远等切后台再换,不在他手上有活的时候打断。
   */
  const applyNow = async (): Promise<void> => {
    const c = await getCtx();
    await c?.updater.reload();
  };

  /**
   * App 挂载后调一次。
   *
   * ★ `notifyAppReady()` 放在最前面、不等任何网络:它的意思是「这个包能跑起来」,
   *   而 App 组件已经挂上了就说明能跑。等接口回来再报,
   *   弱网下超过 10 秒就会被误判成坏包,好好的新版本被退回去。
   */
  const start = async (): Promise<void> => {
    if (started) return;
    started = true;

    const c = await getCtx();
    if (!c) return;
    await c.updater.notifyAppReady().catch(() => undefined);
    if (!enabled) return;

    const auto = () =>
      void checkNow().catch((e: unknown) => console.warn('[liveUpdate] 检查失败', e));
    auto();
    /*
     * ★ 开着不动也要查。调度可能一整天开着管理端、从不切后台 ——
     *   只靠「启动 / 回前台」的话,他永远等不到新版本,也看不到提示。
     */
    setInterval(auto, deps.intervalMs ?? 30 * 60_000);
    await deps.onAppResume(auto);
  };

  /** 提示条上点了「稍后」:这个版本不再提示(切后台时照样会自动换上) */
  const dismiss = () => {
    if (status.kind === 'none') return;
    dismissed.add(status.version);
    setStatus({ ...status, dismissed: true });
  };

  const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => void listeners.delete(l);
  };

  return {
    enabled,
    checkNow,
    applyNow,
    start,
    dismiss,
    subscribe,
    getStatus: () => status,
  };
}
